/**
 * Checkout Page — Redirect to External Checkout App
 *
 * The full checkout flow (Stripe, shipping, discounts, upsells) lives in the
 * storefront-checkout app deployed on a separate CloudFront distribution.
 *
 * This page redirects users who land on the internal /checkout/:token route.
 * It shows the cart summary while the redirect is in progress, so the user
 * sees meaningful content instead of a blank page during navigation.
 */

'use client';

import { useEffect } from 'react';
import styled from '@emotion/styled';
import { useParams, Link } from 'react-router-dom';
import { useCart } from '@/lib/hooks/use-cart';
import { useIntl } from '@/lib/intl';
import { formatPrice } from '@template-project/afrodita-components';
import { env } from '@/lib/utils/env';
import { trackCheckoutStarted } from '@/lib/analytics';
import { resolveRuntimeAppUrl } from '@/lib/utils/runtime-app-url';

const Container = styled.div`
  max-width: 720px;
  margin: 0 auto;
  padding: 40px 24px;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 8px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 32px;
`;

const ItemRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.color};
`;

const ItemImage = styled.img`
  width: 64px;
  height: 64px;
  object-fit: cover;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.background.secondary};
`;

const ItemDetails = styled.div`
  flex: 1;
`;

const ItemName = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ItemMeta = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: 2px;
`;

const ItemPrice = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 20px 0;
  font-size: 18px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const BackLink = styled(Link)`
  display: inline-block;
  margin-top: 24px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 14px;
  text-decoration: underline;
`;

const RedirectNotice = styled.div`
  background: ${({ theme }) => theme.colors.background.secondary};
  padding: 20px;
  border-radius: 8px;
  margin-top: 24px;
  text-align: center;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
`;

export function CheckoutPage() {
  const { token } = useParams<{ token: string }>();
  const { countryConfig } = useIntl();
  const { data: cart, isLoading } = useCart(token ?? null);

  useEffect(() => {
    if (token) {
      trackCheckoutStarted({ cartToken: token });
      window.location.assign(resolveRuntimeAppUrl(`${env.CHECKOUT_URL}/checkout/${token}`));
    }
  }, [token]);

  const priceOptions = {
    currency: countryConfig.currency.code,
    locale: countryConfig.locale,
    symbolOverride: countryConfig.currency.format,
  };

  if (isLoading) {
    return (
      <Container>
        <Title>Checkout</Title>
        <p>Loading your cart...</p>
      </Container>
    );
  }

  if (!cart || cart.items.length === 0) {
    return (
      <Container>
        <Title>Checkout</Title>
        <p>Your cart is empty.</p>
        <BackLink to="/">Continue Shopping</BackLink>
      </Container>
    );
  }

  return (
    <Container>
      <Title>Checkout</Title>
      <Subtitle>Order summary — {cart.items.length} item{cart.items.length !== 1 ? 's' : ''}</Subtitle>

      {cart.items.map((item) => (
        <ItemRow key={`${item.product_id}-${item.variant_id}`}>
          {item.image_url ? (
            <ItemImage src={item.image_url} alt={item.name} />
          ) : (
            <div style={{ width: 64, height: 64, borderRadius: 4, background: '#eee' }} />
          )}
          <ItemDetails>
            <ItemName>{item.name}</ItemName>
            <ItemMeta>Qty: {item.quantity}</ItemMeta>
          </ItemDetails>
          <ItemPrice>{formatPrice(item.price.display, priceOptions)}</ItemPrice>
        </ItemRow>
      ))}

      <TotalRow>
        <span>Total</span>
        <span>{formatPrice(cart.totals.total.display, priceOptions)}</span>
      </TotalRow>

      <RedirectNotice>
        Redirecting to secure checkout...
      </RedirectNotice>

      <BackLink to="/">← Continue Shopping</BackLink>
    </Container>
  );
}
